import { motion } from "framer-motion";
import {
  AlertCircle,
  ArrowLeft,
  Copy,
  Key,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Brain,
} from "../../components/icons";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { Toast } from "../../components/Toast";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';

interface SearchableSelectProps {
  options: Array<{ id: string; userId: string; user: { fullName: string; email: string } }>;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  required?: boolean;
  disabled?: boolean;
}

function SearchableSelect({ options, value, onChange, placeholder, required, disabled }: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');

  React.useEffect(() => {
    if (value) {
      const selected = options.find(option => option.userId === value);
      if (selected) {
        setSelectedLabel(`${selected.user.fullName} (${selected.user.email})`);
      }
    } else {
      setSelectedLabel('');
    }
  }, [value, options]);

  const filteredOptions = options.filter(option =>
    option.user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option: typeof options[0]) => {
    onChange(option.userId);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setSearchTerm('');
  };

  const handleInputBlur = () => {
    setTimeout(() => setIsOpen(false), 200);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={isOpen ? searchTerm : selectedLabel}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="freud-input cursor-pointer"
      />

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-cream-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-chocolate-400 text-center">
              No coaches found
            </div>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option.id}
                onClick={() => handleSelect(option)}
                className="px-4 py-3 hover:bg-cream-100 cursor-pointer border-b border-cream-200 last:border-b-0"
              >
                <div className="font-medium text-chocolate-900">{option.user.fullName}</div>
                <div className="text-sm text-chocolate-600">{option.user.email}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

interface AssessmentCode {
  id: string;
  coachId: string;
  code: string;
  createdAt: Date;
  used: boolean;
  coach: {
    user: {
      fullName: string;
      email: string;
    };
  };
}

const columnHelper = createColumnHelper<AssessmentCode>();

export function AssessmentCodes() {
  const [codes, setCodes] = useState<AssessmentCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState("");
  const [coaches, setCoaches] = useState<any[]>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [filterByTier, setFilterByTier] = useState("all");
  const [filterByStatus, setFilterByStatus] = useState("all");
  const { user } = useAuth();
  const [lastGeneratedCode, setLastGeneratedCode] = useState<string>("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [copyToastMessage, setCopyToastMessage] = useState("");
  const [autoAiAnalysis, setAutoAiAnalysis] = useState(false);

  const columns = React.useMemo(() => [
    columnHelper.accessor('code', {
      header: 'Assessment Code',
      cell: (info) => {
        const code = info.getValue();
        const isNew = code === lastGeneratedCode && showSuccessMessage;
        return (
          <div className="flex items-center gap-2">
            <span className="font-mono text-lg font-medium text-chocolate-900">
              {code}
            </span>
            {isNew && (
              <span className="bg-sage-500 text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse">
                NEW
              </span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('coach.user.fullName', {
      header: 'Assigned Coach',
      cell: (info) => (
        <div className="text-chocolate-900">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('coach.user.email', {
      header: 'Coach Email',
      cell: (info) => (
        <div className="text-chocolate-600">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('used', {
      header: 'Status',
      cell: (info) => (
        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
          info.getValue()
            ? 'bg-coral-100 text-coral-800'
            : 'bg-sage-100 text-sage-800'
        }`}>
          {info.getValue() ? 'Used' : 'Available'}
        </span>
      ),
      filterFn: (row, columnId, value) => {
        if (value === 'all') return true;
        const isUsed = row.getValue(columnId);
        return (value === 'used' ? isUsed : !isUsed) as boolean;
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created Date',
      cell: (info) => {
        const date = info.getValue();
        return (
          <div className="text-chocolate-600">
            <div>{date.toLocaleDateString()}</div>
            <div className="text-sm text-chocolate-400">
              {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const code = info.row.original;
        return (
          <button
            onClick={() => copyToClipboard(code.code)}
            className="p-2 rounded-lg bg-sage-100 text-sage-600 hover:bg-sage-200 transition-all"
            title="Copy Code"
          >
            <Copy className="w-4 h-4" />
          </button>
        );
      },
    }),
  ], [lastGeneratedCode, showSuccessMessage]);

  const filteredData = React.useMemo(() => {
    return codes.filter(code => {
      if (filterByTier !== "all" && (code as any).tier !== filterByTier) {
        return false;
      }

      if (filterByStatus === "used" && !code.used) {
        return false;
      }
      if (filterByStatus === "available" && code.used) {
        return false;
      }

      return true;
    });
  }, [codes, filterByTier, filterByStatus]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  useEffect(() => {
    if (user?.id) {
      loadCodes();
      loadCoaches();
    }
  }, [user]);

  const loadCodes = async () => {
    try {
      setIsLoading(true);

      const { data: codesData, error: codesError } = await supabase
        .from('assessment_codes')
        .select(`
          id,
          code,
          coach_id,
          is_used,
          created_at
        `)
        .order('created_at', { ascending: false });

      if (codesError) throw codesError;

      const codesWithCoaches = await Promise.all(
        (codesData || []).map(async (codeData) => {
          let coachInfo = { fullName: '', email: '' };

          if (codeData.coach_id) {
            const { data: coachData } = await supabase
              .from('coaches')
              .select('user_id')
              .eq('id', codeData.coach_id)
              .maybeSingle();

            if (coachData?.user_id) {
              const { data: userData } = await supabase
                .from('users')
                .select('full_name, email')
                .eq('id', coachData.user_id)
                .maybeSingle();

              if (userData) {
                coachInfo = {
                  fullName: userData.full_name || '',
                  email: userData.email || '',
                };
              }
            }
          }

          return {
            id: codeData.id,
            coachId: codeData.coach_id || '',
            code: codeData.code,
            createdAt: new Date(codeData.created_at || Date.now()),
            used: codeData.is_used || false,
            coach: {
              user: coachInfo,
            },
          };
        })
      );

      setCodes(codesWithCoaches);
    } catch (err) {
      console.error("Error loading codes:", err);
      setError("Failed to load assessment codes");
    } finally {
      setIsLoading(false);
    }
  };

  const loadCoaches = async () => {
    try {
      const { data: coachesData, error: coachesError } = await supabase
        .from('coaches')
        .select('id, user_id');

      if (coachesError) throw coachesError;

      const coachesWithUsers = await Promise.all(
        (coachesData || []).map(async (coach) => {
          const { data: userData } = await supabase
            .from('users')
            .select('full_name, email')
            .eq('id', coach.user_id)
            .maybeSingle();

          return {
            id: coach.id,
            userId: coach.user_id,
            user: {
              fullName: userData?.full_name || '',
              email: userData?.email || '',
            },
          };
        })
      );

      setCoaches(coachesWithUsers);
    } catch (err) {
      console.error("Error loading coaches:", err);
    }
  };

  const generateCode = () => {
    return "HPOS" + Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleGenerateCodes = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);

      const newCode = generateCode();

      const selectedCoachData = coaches.find(c => c.userId === selectedCoach);
      const coachId = selectedCoachData?.id;

      const { error: insertError } = await supabase
        .from('assessment_codes')
        .insert({
          coach_id: coachId,
          code: newCode,
          is_used: false,
          auto_ai_analysis: autoAiAnalysis,
        });

      if (insertError) throw insertError;

      setLastGeneratedCode(newCode);
      setShowSuccessMessage(true);
      setAutoAiAnalysis(false);

      setShowGenerateModal(false);
      loadCodes();

      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 10000);
    } catch (err) {
      console.error("Error generating code:", err);
      setError("Failed to generate assessment code");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopyToastMessage(`Code ${code} copied successfully!`);
      setShowCopyToast(true);
    } catch (error) {
      console.error('Failed to copy code:', error);
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopyToastMessage(`Code ${code} copied successfully!`);
      setShowCopyToast(true);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading assessment codes..." />;
  }

  return (
    <div className="min-h-screen freud-bg py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-chocolate-600 hover:text-sage-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-lavender-500 rounded-2xl flex items-center justify-center shadow-soft">
              <Key className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-chocolate-900">
                Assessment Codes
              </h1>
              <p className="text-chocolate-500">
                Generate and manage assessment codes
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="freud-btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Generate Code
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-coral-100 text-coral-700 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {showSuccessMessage && lastGeneratedCode && (
          <div className="bg-sage-50 text-sage-700 p-6 rounded-xl mb-6 border border-sage-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-2">Assessment Code Generated Successfully!</h3>
                <p className="text-sage-700">
                  New code: <span className="font-mono text-xl font-bold bg-sage-100 px-3 py-1 rounded">{lastGeneratedCode}</span>
                </p>
                <p className="text-sage-600 text-sm mt-2">This code appears at the top of the list below.</p>
              </div>
              <button
                onClick={() => {
                  setShowSuccessMessage(false);
                }}
                className="text-sage-600 hover:text-sage-700 text-xl"
              >
                x
              </button>
            </div>
          </div>
        )}

        <div className="freud-card-elevated p-6 mb-8">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-chocolate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search codes..."
                  value={globalFilter ?? ''}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="freud-input pl-10"
                />
              </div>
            </div>

            <div>
              <select
                value={filterByTier}
                onChange={(e) => setFilterByTier(e.target.value)}
                className="freud-input"
              >
                <option value="all">All Tiers</option>
                <option value="basic">Basic</option>
                <option value="basic_plus">Basic Plus</option>
                <option value="advanced">Advanced</option>
                <option value="partner">Partner</option>
              </select>
            </div>

            <div>
              <select
                value={filterByStatus}
                onChange={(e) => setFilterByStatus(e.target.value)}
                className="freud-input"
              >
                <option value="all">All Status</option>
                <option value="available">Available</option>
                <option value="used">Used</option>
              </select>
            </div>
          </div>
        </div>

        <div className="freud-card-elevated overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream-100 border-b border-cream-200">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-6 py-4 text-left text-sm font-semibold text-chocolate-900 cursor-pointer hover:bg-cream-200"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {{
                            asc: ' ↑',
                            desc: ' ↓',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-cream-200">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-chocolate-400">
                      {codes.length === 0 ? "No assessment codes found" : "No codes match your search criteria"}
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => {
                    const isNew = row.original.code === lastGeneratedCode && showSuccessMessage;
                    return (
                      <motion.tr
                        key={row.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`hover:bg-cream-50 transition-colors ${
                          isNew ? 'bg-sage-50 border-l-4 border-sage-500' : ''
                        }`}
                      >
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-cream-200 bg-cream-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-chocolate-600">
                <span>
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )}{' '}
                  of {table.getFilteredRowModel().rows.length} results
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-cream-300 text-chocolate-600 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>

                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-cream-300 text-chocolate-600 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-chocolate-600">Page</span>
                  <input
                    type="number"
                    value={table.getState().pagination.pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value ? Number(e.target.value) - 1 : 0;
                      table.setPageIndex(page);
                    }}
                    className="w-16 px-2 py-1 text-center border border-cream-300 rounded text-sm"
                    min="1"
                    max={table.getPageCount()}
                  />
                  <span className="text-sm text-chocolate-600">of {table.getPageCount()}</span>
                </div>

                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-cream-300 text-chocolate-600 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-cream-300 text-chocolate-600 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>

                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                  className="px-3 py-2 border border-cream-300 rounded-lg text-sm bg-white"
                >
                  {[5, 10, 20, 50].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {showGenerateModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="freud-card-elevated p-8 w-full max-w-lg mx-4"
            >
              <h2 className="text-2xl font-bold text-chocolate-900 mb-6">
                Generate Assessment Code
              </h2>
              <form onSubmit={handleGenerateCodes} className="space-y-6">
                <div>
                  <label htmlFor="coach" className="block text-chocolate-900 mb-2">
                    Assign to Coach
                  </label>
                  <SearchableSelect
                    options={coaches}
                    value={selectedCoach}
                    onChange={setSelectedCoach}
                    placeholder="Search for a coach by name or email..."
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="bg-cream-100 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-sage-100 rounded-lg">
                        <Brain className="w-5 h-5 text-sage-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-chocolate-800">Auto AI Analysis</p>
                        <p className="text-xs text-chocolate-500">
                          Respondents get instant AI analysis after completing their assessment
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAutoAiAnalysis(!autoAiAnalysis)}
                      className="flex-shrink-0"
                    >
                      <div
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          autoAiAnalysis ? 'bg-sage-500' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                            autoAiAnalysis ? 'translate-x-[22px]' : 'translate-x-0.5'
                          }`}
                        />
                      </div>
                    </button>
                  </div>
                </div>
                <div className="flex justify-end gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowGenerateModal(false); setAutoAiAnalysis(false); }}
                    className="freud-btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="freud-btn-primary"
                  >
                    {isLoading ? "Generating..." : "Generate Code"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>

      <Toast
        isVisible={showCopyToast}
        message={copyToastMessage}
        type="success"
        onClose={() => setShowCopyToast(false)}
      />
    </div>
  );
}
